import { pageTitle } from '@palaccount/config';
import type { PayrollRunResponse } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useCreatePayrollRun,
  useGeneratePayslips,
  usePayrollAction,
  usePayrollApprovers,
  usePayrollRun,
  usePayrollRuns,
  usePayslips,
  useRejectPayrollRun,
  useUpdatePayrollRun,
} from './api';
import { HrTabs } from './hr-tabs';
import { PersonSourceHint } from './person-source';

/** HR-02's run rail: one block at a time (NFR-42). */
const RUNS_PAGE_SIZE = 25;
/** §5.9's batch is one card per employee, so a page is a screenful of cards. */
const PAYSLIPS_PAGE_SIZE = 25;

/**
 * HR-02 — Payroll Run: period → tick people → review → submit → approve →
 * post → LOCKED, then payslips. The whole screen reaches the API through
 * payroll.* routes — a caller without payroll.view never sees a number here
 * (R15), and this tab is not even offered to them (HrTabs).
 */
export function PayrollPage() {
  const { t } = useTranslation();
  // NFR-42: the rail is a register that gains a row every month and loses
  // none, and every row costs a read of that run's lines to total it.
  const paging = usePagedState(RUNS_PAGE_SIZE);
  const { data, isPending } = usePayrollRuns(true, paging.page, RUNS_PAGE_SIZE);
  const range = usePageRange(data, RUNS_PAGE_SIZE);
  const runs = data?.runs;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const runId = selectedId ?? runs?.[0]?.id ?? null;
  const run = usePayrollRun(runId);

  useEffect(() => {
    document.title = pageTitle(t('hr.payrollTitle'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <HrTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('hr.payrollTitle')}</h2>
        <Gated permission="payroll.edit" reasonKey="perm.cannot.createPayrollRuns">
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden /> {t('hr.newRun')}
          </Button>
        </Gated>
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {notice ? <p className="rounded-md bg-primary/10 p-3 text-sm">{notice}</p> : null}

      {creating ? (
        <NewRunCard
          onCancel={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelectedId(id);
          }}
          onError={setError}
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <Card>
          <CardContent className="grid gap-1 p-2">
            {isPending ? (
              <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy />
            ) : (runs ?? []).length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">{t('hr.noRuns')}</p>
            ) : (
              (runs ?? []).map((summary) => (
                <button
                  key={summary.id}
                  type="button"
                  onClick={() => setSelectedId(summary.id)}
                  className={cn(
                    'grid gap-0.5 rounded-md px-2 py-2 text-start hover:bg-accent',
                    runId === summary.id && 'bg-accent',
                  )}
                >
                  <span className="flex items-center justify-between text-sm font-medium">
                    <Ltr value={summary.code} className="font-mono" />
                    <RunStatusPill status={summary.status} />
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <Ltr value={`${summary.startDate} → ${summary.endDate}`} className="font-mono" />
                  </span>
                </button>
              ))
            )}
            {range.pages > 1 ? (
              <div className="px-2 pb-1 pt-2">
                <Pager
                  page={paging.page}
                  pages={range.pages}
                  first={range.first}
                  last={range.last}
                  total={range.total}
                  onPage={paging.setPage}
                  label={t('hr.payrollTitle')}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        {run.data ? (
          <RunDetail run={run.data} onError={setError} onNotice={setNotice} />
        ) : (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-muted-foreground">{t('hr.selectRun')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function RunStatusPill({ status }: { status: PayrollRunResponse['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'draft'
      ? 'bg-muted text-muted-foreground'
      : status === 'pending_approval'
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
        : status === 'approved'
          ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
          : status === 'posted'
            ? 'bg-green-500/10 text-green-700 dark:text-green-400'
            : 'bg-destructive/10 text-destructive';
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', tone)}>
      {t(`hr.run.${status}`)}
    </span>
  );
}

function NewRunCard({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePayrollRun();
  // G-09: a new run's dates start as the company's today.
  const [startDate, setStartDate] = useDocumentDate();
  const [endDate, setEndDate] = useDocumentDate();
  const [payDate, setPayDate] = useDocumentDate();

  const complete = startDate !== '' && endDate !== '' && payDate !== '' && startDate <= endDate;

  return (
    <Card>
      <CardContent className="grid gap-3">
        <h3 className="text-sm font-semibold">{t('hr.newRun')}</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-1">
            <Label htmlFor="run-start">{t('hr.startDate')}</Label>
            <Input id="run-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="run-end">{t('hr.endDate')}</Label>
            <Input id="run-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="run-pay" className="text-primary">
              {t('hr.payDate')}
            </Label>
            <Input id="run-pay" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            disabled={!complete}
            onClick={() => {
              onError(null);
              create.mutate(
                { startDate, endDate, payDate },
                {
                  onSuccess: (created) => onCreated(created.id),
                  onError: (cause) => onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                },
              );
            }}
          >
            {t('common.save')}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RunDetail({
  run,
  onError,
  onNotice,
}: {
  run: PayrollRunResponse;
  onError: (message: string | null) => void;
  onNotice: (message: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const canEdit = useCan('payroll.edit');
  const approvers = usePayrollApprovers(true);
  const updateRun = useUpdatePayrollRun();
  const action = usePayrollAction();
  const rejectRun = useRejectPayrollRun();
  const generatePayslips = useGeneratePayslips();
  /*
   * The RUNS register above already owns `?page=`, so this second list on the
   * same screen carries its own name — `?payslipPage=` (PAGINATION.md §3
   * step 12). Page 1 still deletes its own parameter, so the canonical link to
   * a run has no query string whichever block was scrolled.
   */
  const payslipPaging = usePagedState(PAYSLIPS_PAGE_SIZE, 'payslip');
  const payslips = usePayslips(
    run.id,
    run.payslipsGeneratedAt !== null,
    payslipPaging.page,
    PAYSLIPS_PAGE_SIZE,
  );
  const payslipRange = usePageRange(payslips.data, PAYSLIPS_PAGE_SIZE);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  /** The approver just chosen, held until the server echoes it back — a
   *  server-controlled select otherwise snaps back to blank mid-write. */
  const [pendingApprover, setPendingApprover] = useState<string | null>(null);
  const baseCurrency = useBaseCurrency();

  /**
   * `lines` and `totals` are OPTIONAL on the wire, and only for one caller:
   * an `hr.approve` approver who does not hold `payroll.view` gets a decision
   * response with the pay figures ABSENT (BR-PERM-40). That caller never
   * reaches this screen — `/employees/payroll` is behind the `payroll` area in
   * route-permissions.ts and the HR tab is drawn only for `payroll.view` — so
   * the fallbacks below are the type system's due, not a state to design for.
   */
  const lines = run.lines ?? [];
  const totals = run.totals ?? {
    // BR-HR-02-07 converts every line into the base before adding, so the
    // TOTALS are always in the company's own money (GEN-107) — read from the
    // session, so even this unreachable fallback names it correctly.
    currency: baseCurrency,
    base: '0.00',
    allowances: '0.00',
    deductions: '0.00',
    net: '0.00',
    includedCount: 0,
  };

  const locked = run.status === 'posted' || run.status === 'cancelled';
  const editable = canEdit && !locked;
  const onErrorCb = (cause: unknown) =>
    onError(cause instanceof Error ? cause.message : t('common.saveFailed'));

  return (
    <div className="grid content-start gap-4">
      {/* The locked banner (§7 sentences, verbatim from the contract). */}
      {run.status === 'posted' ? (
        <p className="rounded-md bg-green-500/10 p-3 text-sm">🔒 {t('hr.postedLocked')}</p>
      ) : run.status === 'cancelled' ? (
        <p className="rounded-md bg-destructive/10 p-3 text-sm">{t('hr.cancelledLocked')}</p>
      ) : null}
      {run.rejectionReason && run.status === 'draft' ? (
        <p className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
          {t('hr.rejectionReason')}: {run.rejectionReason}
        </p>
      ) : null}

      <Card>
        <CardContent className="grid gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="me-auto font-semibold">
              <Ltr value={run.code} className="font-mono" />
            </h3>
            <RunStatusPill status={run.status} />
          </div>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">{t('hr.period')}</dt>
              <dd className="font-mono">
                <Ltr value={`${run.startDate} → ${run.endDate}`} />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('hr.payDate')}</dt>
              <dd className="rounded bg-primary/10 px-1 font-mono">{run.payDate}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('hr.includedCount')}</dt>
              <dd className="font-mono">{totals.includedCount}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead>{t('hr.employee')}</TableHead>
                <TableHead className="text-end">{t('hr.baseSalary')}</TableHead>
                <TableHead className="text-end">{t('hr.allowances')}</TableHead>
                <TableHead className="text-end">{t('hr.deductions')}</TableHead>
                <TableHead className="text-end">{t('hr.netPay')}</TableHead>
                <TableHead>{t('hr.bank')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id} className={cn(!line.included && 'opacity-40')}>
                  <TableCell>
                    <Checkbox
                      aria-label={`${t('hr.includeInRun')} ${line.fullName}`}
                      checked={line.included}
                      disabled={!editable}
                      onCheckedChange={(checked) =>
                        updateRun.mutate(
                          {
                            runId: run.id,
                            body: { lines: [{ employeeId: line.employeeId, included: checked === true }] },
                          },
                          { onError: onErrorCb },
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <span className="block text-sm">{ar ? line.fullNameAr : line.fullName}</span>
                    <span className="text-xs text-muted-foreground">{t(`hr.dept.${line.department}`)}</span>
                  </TableCell>
                  <TableCell className="text-end">
                    <Amount value={line.baseSalary} currency={line.currency} />
                  </TableCell>
                  <TableCell className="text-end">
                    {editable ? (
                      <LineAmountEditor
                        value={line.allowances}
                        label={`${t('hr.allowances')} ${line.fullName}`}
                        onSave={(allowances) =>
                          updateRun.mutate(
                            { runId: run.id, body: { lines: [{ employeeId: line.employeeId, allowances }] } },
                            { onError: onErrorCb },
                          )
                        }
                      />
                    ) : (
                      <Amount value={line.allowances} currency={line.currency} />
                    )}
                  </TableCell>
                  <TableCell className="text-end">
                    {editable ? (
                      <LineAmountEditor
                        value={line.deductions}
                        label={`${t('hr.deductions')} ${line.fullName}`}
                        onSave={(deductions) =>
                          updateRun.mutate(
                            { runId: run.id, body: { lines: [{ employeeId: line.employeeId, deductions }] } },
                            { onError: onErrorCb },
                          )
                        }
                      />
                    ) : (
                      <Amount value={line.deductions} currency={line.currency} />
                    )}
                  </TableCell>
                  <TableCell className="text-end font-medium">
                    <Amount value={line.net} currency={line.currency} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t(`hr.method.${line.paymentMethod}`)}
                    {line.bankAccountName ? ` · ${line.bankAccountName}` : ''}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell />
                <TableCell>
                  {t('hr.totals')}{' '}
                  <span className="text-xs text-muted-foreground">{t('hr.totalsInBase')}</span>
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={totals.base} currency={totals.currency} />
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={totals.allowances} currency={totals.currency} />
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={totals.deductions} currency={totals.currency} />
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={totals.net} currency={totals.currency} />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approval */}
      <Card>
        <CardContent className="grid gap-3">
          <h3 className="text-sm font-semibold">{t('hr.approval')}</h3>
          <div className="flex flex-wrap items-center gap-3">
            <NativeSelect
              value={pendingApprover ?? run.approverEmployeeId ?? ''}
              disabled={!editable}
              onChange={(e) => {
                const chosen = e.target.value;
                setPendingApprover(chosen);
                updateRun.mutate(
                  { runId: run.id, body: { approverEmployeeId: chosen || null } },
                  { onError: onErrorCb, onSettled: () => setPendingApprover(null) },
                );
              }}
              className="h-10 w-72"
              aria-label={t('hr.approver')}
            >
              <option value="">{t('hr.chooseApprover')}</option>
              {(approvers.data ?? []).map((approver) => (
                <option key={approver.employeeId} value={approver.employeeId}>
                  {ar ? approver.fullNameAr : approver.fullName} — {approver.jobTitle}
                </option>
              ))}
            </NativeSelect>
            {/* BR-HR-01-16: EMPLOYEES, through the approver surface — not the
                user-account list, and the two can differ (BR-HR-01-15). */}
            <PersonSourceHint source="employees" surface="approver" />
            {run.approvalDate ? (
              <span className="rounded-full bg-green-500/10 px-2 py-1 text-xs text-green-700 dark:text-green-400">
                {t('hr.approvedOn')} <Ltr value={run.approvalDate} className="font-mono" />
              </span>
            ) : null}
            {run.status === 'draft' ? (
              <Gated permission="payroll.edit" reasonKey="perm.cannot.editPayrollRuns">
              <Button
                // Every edit on this card — the approver above included — is a
                // fire-and-forget write against a server-controlled field.
                // Submitting on top of one in flight asks the server about a
                // run it has not finished saving, and BR-HR-02-08's approver
                // check then refuses a run whose approver WAS chosen.
                disabled={updateRun.isPending}
                onClick={() => {
                  onError(null);
                  action.mutate({ runId: run.id, action: 'submit' }, { onError: onErrorCb });
                }}
              >
                {t('hr.submitForApproval')}
              </Button>
              </Gated>
            ) : null}
            {run.status === 'pending_approval' ? (
              <>
                <Gated permission="hr.approve" reasonKey="perm.cannot.decidePayrollRuns">
                  <Button
                    onClick={() => {
                      onError(null);
                      action.mutate({ runId: run.id, action: 'approve' }, { onError: onErrorCb });
                    }}
                  >
                    {t('hr.approve')}
                  </Button>
                </Gated>
                <Gated permission="hr.approve" reasonKey="perm.cannot.decidePayrollRuns">
                  <Button variant="outline" onClick={() => setRejecting(true)}>
                    {t('hr.reject')}
                  </Button>
                </Gated>
              </>
            ) : null}
          </div>
          {rejecting ? (
            <div className="grid gap-2">
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('hr.rejectRunHint')}
                rows={2}
              />
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={rejectReason.trim() === ''}
                  onClick={() => {
                    onError(null);
                    rejectRun.mutate(
                      { runId: run.id, reason: rejectReason },
                      {
                        onSuccess: () => {
                          setRejecting(false);
                          setRejectReason('');
                        },
                        onError: onErrorCb,
                      },
                    );
                  }}
                >
                  {t('hr.reject')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRejecting(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Posting */}
      <Card>
        <CardContent className="grid gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Posted and cancelled runs are locked for ever (R8) — the button
                is gone for the Owner too. "Not approved yet" is a lifecycle
                RULE and keeps the button live-and-disabled (16.7 row 3); the
                permission greys it and says who to ask (row 2). */}
            {/* ERR-HR-02-02 — "clicked Post payroll twice: the button greys
                after the first press" — and this build is STRICTER than the
                row describes: once `status === 'posted'` the button is not
                greyed, it is not rendered at all. The screen is not the wall
                either; `PayrollService.post()` refuses a run that is not
                `approved`, so a second press that reached the server is
                refused there too. Recorded as a spec-versus-build wording
                difference (gone, not greyed), with the row's own outcome —
                a second post cannot happen — met. */}
            {run.status !== 'posted' && run.status !== 'cancelled' ? (
              <Gated permission="payroll.approve" reasonKey="perm.cannot.postPayroll">
              <Button
                disabled={run.status !== 'approved'}
                onClick={() => {
                  onError(null);
                  action.mutate(
                    { runId: run.id, action: 'post' },
                    {
                      onSuccess: (body) => onNotice((body as { message?: string }).message ?? null),
                      onError: onErrorCb,
                    },
                  );
                }}
              >
                {t('hr.postPayroll')}
              </Button>
              </Gated>
            ) : null}
            {run.status === 'posted' && run.payslipsGeneratedAt === null ? (
              <Gated permission="payroll.edit" reasonKey="perm.cannot.generatePayslips">
              <Button
                variant="outline"
                onClick={() => {
                  onError(null);
                  generatePayslips.mutate(run.id, {
                    onSuccess: (body) => onNotice(body.message),
                    onError: onErrorCb,
                  });
                }}
              >
                {t('hr.generatePayslips')}
              </Button>
              </Gated>
            ) : null}
            {run.status !== 'posted' && run.status !== 'cancelled' ? (
              <Gated permission="payroll.edit" reasonKey="perm.cannot.cancelPayrollRuns">
                <Button
                  variant="outline"
                  onClick={() => {
                    onError(null);
                    action.mutate({ runId: run.id, action: 'cancel' }, { onError: onErrorCb });
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </Gated>
            ) : null}
          </div>
          {/* §6's post hint — hidden once posting is possible. */}
          {run.status !== 'approved' && run.status !== 'posted' && run.status !== 'cancelled' ? (
            <p className="text-xs text-muted-foreground">{t('hr.postHint')}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* Payslips — only after posting (BR-HR-02-13). */}
      {run.payslipsGeneratedAt !== null && payslips.data ? (
        <Card data-testid="payslips">
          <CardContent className="grid gap-2">
            <h3 className="text-sm font-semibold">{t('hr.payslips')}</h3>
            {payslips.data.payslips.map((payslip) => (
              <div
                key={payslip.employeeId}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border p-2 text-sm"
              >
                <Ltr value={payslip.code} className="font-mono text-xs text-muted-foreground" />
                <span className="me-auto">{ar ? payslip.fullNameAr : payslip.fullName}</span>
                <span className="text-xs text-muted-foreground">
                  {payslip.baseSalary} + {payslip.allowances} − {payslip.deductions}
                </span>
                <Amount value={payslip.net} currency={payslip.currency} className="font-medium" />
              </div>
            ))}
            <Pager
              page={payslipPaging.page}
              pages={payslipRange.pages}
              first={payslipRange.first}
              last={payslipRange.last}
              total={payslipRange.total}
              onPage={payslipPaging.setPage}
              label={t('hr.payslips')}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function LineAmountEditor({
  value,
  label,
  onSave,
}: {
  value: string;
  label: string;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Input
      aria-label={label}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value && /^\d{1,13}(\.\d{1,2})?$/.test(draft)) onSave(draft);
        else setDraft(value);
      }}
      className="ms-auto h-8 w-28 text-end font-mono"
    />
  );
}
